import { useEffect, useState } from "react";

import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { runAndRecordAssessment } from "../../data/api/botpressAssessmentRunner.js";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";

const PHASE = {
  ready: "ready",
  running: "running",
  done: "done",
  failed: "failed",
};

const BotpressExpertRunDialog = props => {
  const {
    open,
    onClose,
    botODM,
    userODM,
    userData,
    configurations,
    testConfigurations,
    questions = [],
    fileName = "Uploaded file",
    onAssessmentCreated,
    maxWidth = "sm",
    fullWidth = true,
  } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [phase, setPhase] = useState(PHASE.ready);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");

  const assessmentName = fileName.replace(/\.[^.]+$/, "") || "Custom Assessment";

  useEffect(() => {
    if (open) {
      setPhase(PHASE.ready);
      setProgress(0);
      setStatusText(
        `${questions.length} question${questions.length === 1 ? "" : "s"} ready to run.`,
      );
    }
  }, [open, questions.length]);

  const isRunning = phase === PHASE.running;

  const handleRun = async () => {
    setPhase(PHASE.running);
    setProgress(0);
    setStatusText("Starting…");

    try {
      const result = await runAndRecordAssessment({
        botODM,
        questions,
        userData,
        userODM,
        configurations,
        testConfigurations,
        assessmentType: "Custom",
        assessmentName,
        comment: `Expert Testing run from "${fileName}"`,
        onProgress: event => {
          if (event.phase === "sending") {
            setProgress(Math.round((event.index / event.total) * 100));
            setStatusText(`Question ${event.index + 1} of ${event.total}…`);
          }
        },
      });

      setProgress(100);

      if (result.assessmentCreated) {
        setPhase(PHASE.done);
        setStatusText(
          `Done. ${questions.length} question${questions.length === 1 ? "" : "s"} replayed and the assessment was created.`,
        );
        enqueueSnackbar("Assessment created", { variant: "success" });
        if (typeof onAssessmentCreated === "function") {
          onAssessmentCreated();
        }
      } else {
        setPhase(PHASE.failed);
        setStatusText(
          `The conversation ran, but the assessment could not be saved: ${result.assessmentError.message}`,
        );
        enqueueSnackbar("Ran, but the assessment was not saved", { variant: "warning" });
      }
    } catch (error) {
      console.error("Expert Testing run failed:", error);
      setPhase(PHASE.failed);
      setStatusText(error.message || "The run failed.");
      enqueueSnackbar("Test run failed", { variant: "error" });
    }
  };

  const onCloseHandler = () => {
    if (isRunning) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>Run Your Test</DialogTitle>
      <DialogContent>
        <DialogMessage>
          These questions were found in <strong>{fileName}</strong>. They are sent to the test bot
          in order, and the run is saved as an Assessment.
        </DialogMessage>

        <Box sx={{ marginTop: theme.spacing(1) }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
            {isRunning && <CircularProgress size={18} color="secondary" />}
            {phase === PHASE.done && <CheckCircle fontSize="small" color="success" />}
            {phase === PHASE.failed && <ErrorOutline fontSize="small" color="error" />}
            <Typography variant="body2" color="textSecondary">
              {statusText}
            </Typography>
          </Box>
          {(isRunning || phase === PHASE.done) && (
            <LinearProgress
              color="secondary"
              variant="determinate"
              value={progress}
              sx={{ marginTop: theme.spacing(1), borderRadius: theme.spacing(0.5) }}
            />
          )}
        </Box>

        <Box
          sx={{
            marginTop: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
            maxHeight: "40vh",
            overflowY: "auto",
          }}
        >
          {questions.map((question, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: theme.spacing(1),
                padding: theme.spacing(1),
                borderBottom:
                  index < questions.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
              }}
            >
              <Typography variant="body2" color="textSecondary" sx={{ minWidth: "24px" }}>
                {index + 1}.
              </Typography>
              <Typography variant="body2">{question}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseHandler} color="secondary" variant="contained" disabled={isRunning}>
          {phase === PHASE.done ? "Close" : "Cancel"}
        </Button>
        {phase !== PHASE.done && (
          <Button
            onClick={handleRun}
            color="secondary"
            variant="contained"
            disabled={isRunning || questions.length === 0}
          >
            {isRunning ? "Running…" : phase === PHASE.failed ? "Try Again" : "Run Test"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BotpressExpertRunDialog;
